import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { axiosCMSInstance } from "../utils/axios";

const BannerContext = createContext({
  banners: [],
  bannersMap: {},
});

export const useBannerContext = () => useContext(BannerContext);

export const BannerProvider = ({ children }) => {
  const [banners, setBanners] = useState([]);
  const [bannersMap, setBannersMap] = useState({});

  // keep bannersMap in sync
  useEffect(() => {
    const map = {};
    banners.forEach((b) => {
      map[b.id] = b;
    });
    setBannersMap(map);
  }, [banners]);

  const loadBanners = useCallback(async () => {
    try {
      const response = await axiosCMSInstance.get("/banners", {
        params: {
          populate: "*",
          pagination: {
            page: 1,
            pageSize: 3,
          },
          sort: { publishedAt: "desc" },
        },
      });
      const dataArr = response.data.data;
      const items = dataArr.map((item) => {
        const attrs = item.attributes || {};
        // flatten rich-text description blocks into a plain string
        const descriptionBlocks = Array.isArray(attrs.description) ? attrs.description : [];
        const descriptionText = descriptionBlocks
          .flatMap((block) => {
            return Array.isArray(block.children)
              ? block.children.map((child) => child.text || "")
              : [];
          })
          .join(" ");
        // map CTA relations
        const ctas = (attrs.banner_ctas?.data || []).map((ctaItem) => {
          const cta = ctaItem.attributes || {};
          return {
            text: cta.text || "",
            link: cta.link || "",
            style: cta.style || {},
            icon: cta.icon || "",
          };
        });
        return {
          id: item.id,
          title: typeof attrs.title === "string" ? attrs.title : "",
          renderTitle: attrs.renderTitleAtBanner || false,
          titleStyle: attrs.titleStyle || {},
          description: descriptionText,
          renderDescription: attrs.renderDescriptionAtBanner || false,
          descriptionStyle: attrs.descriptionStyle || {},
          imageUrl: attrs.image?.data?.attributes?.url || "",
          mobileImageUrl: attrs.mobileImage?.data?.attributes?.url || "",
          contentStyle: attrs.contentStyle || {},
          ctas,
        };
      });
      setBanners(items);
    } catch (err) {
      console.error("Error loading banners:", err);
    }
  }, []);

  const loadBanner = useCallback(async (id) => {
    try {
      const response = await axiosCMSInstance.get(`/banners/${id}`, {
        params: { populate: "*" },
      });
      const data = response.data.data;
      const banner = { id: data.id, ...data.attributes };
      setBannersMap((prev) => ({ ...prev, [banner.id]: banner }));
    } catch (err) {
      console.error(`Error loading banner ${id}:`, err);
    }
  }, []);

  return (
    <BannerContext.Provider value={{ banners, bannersMap, loadBanners, loadBanner }}>
      {children}
    </BannerContext.Provider>
  );
};
